import { SearchComparisonDateTime } from "@components/common/calendar/date-range-picker/Interfaces";
import { Tooltip } from "@components/common/tooltip";
import { ComparisonType } from "@components/widgets/base/widget";
import { CalendarModeRangeType, TimeInterval } from "@custom-types/Types";
import { VehicleData } from "@queries/widgets/StoppedVehicleStatistics";
import {
  CHART_TYPE,
  COMPARISON_TYPE,
  ECHART_VEHICLE_STATISTICS_SIZE,
  SERIES_Z_VALUE,
  TIME_INTERVAL,
  WIDGET_MAIN_TYPE
} from "@resources/Constants";
import { Theme } from "@themes/Types";
import { convertHexToRgb } from "@utils/Global";
import {
  chartTooltipFormatter,
  getAxisIntervalLabels,
  getPercent,
  xAxisLabelFormatter
} from "@utils/Widget";
import {
  BarSeriesOption,
  DefaultLabelFormatterCallbackParams,
  EChartsOption,
  LineSeriesOption,
  TooltipComponentFormatterCallbackParams
} from "echarts";
import _, { concat, isArray, isNumber, max, sum } from "lodash";
import { renderToString } from "react-dom/server";
import { commonTooltipSettings } from "@components/report/widget/widget-statistics/helper";
import { theme as HVTheme } from "@repo/HVComponents";
import {
  GraphLang,
  StoppedVehicleStatisticsDataPieChart,
  StoppedVehicleStatisticsExpansionChartType,
  StoppedVehicleStatisticsMainChartType,
  StoppedVehicleStatisticsMainType,
  SumVehicle
} from "./Interfaces";
import { BarSeriesDataType, VehicleArrayType } from "./list-vehicle/Interface";

const tooltipSettingLineSummary = (
  timeInterval: TimeInterval,
  end: string,
  mode: CalendarModeRangeType
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      return chartTooltipFormatter(
        timeInterval,
        params[0].name,
        params.map((param) => {
          return {
            label: param.seriesName || "Data",
            data: String(param.data),
            color: String(param.color)
          };
        }),
        end,
        mode
      );
    }
    return "";
  },
  ...commonTooltipSettings()
});

const tooltipSettingLine = (
  timeInterval: TimeInterval,
  mode: CalendarModeRangeType
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      return chartTooltipFormatter(
        timeInterval,
        params[0].name,
        params.map((item) => ({
          label: item.seriesName || "",
          data: String(item.data),
          color: String(item.color)
        })),
        "",
        mode
      );
    }
    return "";
  },
  ...commonTooltipSettings()
});

const tooltipRadar = (
  theme: Theme,
  graphLang: GraphLang,
  sumData: SumVehicle[]
): EChartsOption["tooltip"] => ({
  trigger: "item",
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (params && !isArray(params) && isArray(params.value)) {
      return renderToString(
        Tooltip({
          subjects: [
            {
              name: `${params.name}`,
              nameColor: String(params.borderColor || params.color)
            }
          ],
          infos: [
            {
              name: graphLang?.car || "",
              value: `${sumData[params.dataIndex].car || 0}`,
              percent: `${params.value[0] || 0}`,
              markerColor: theme.widget.stoppedVehicleStatistics.car
            },
            {
              name: graphLang?.bus || "",
              value: `${sumData[params.dataIndex].bus || 0}`,
              percent: `${params.value[1] || 0}`,
              markerColor: theme.widget.stoppedVehicleStatistics.bus
            },
            {
              name: graphLang?.truck || "",
              value: `${sumData[params.dataIndex].truck || 0}`,
              percent: `${params.value[2] || 0}`,
              markerColor: theme.widget.stoppedVehicleStatistics.truck
            },
            {
              name: graphLang?.motorcycle || "",
              value: `${sumData[params.dataIndex].motorcycle || 0}`,
              percent: `${params.value[3] || 0}`,
              markerColor: theme.widget.stoppedVehicleStatistics.motorcycle
            },
            {
              name: graphLang?.bicycle || "",
              value: `${sumData[params.dataIndex].bicycle || 0}`,
              percent: `${params.value[4] || 0}`,
              markerColor: theme.widget.stoppedVehicleStatistics.bicycle
            }
          ]
        })
      );
    }
    return "";
  },
  padding: 0,
  borderWidth: 0,
  borderRadius: 100
});

export const expansionChart = (
  theme: Theme,
  comparisonType: ComparisonType,
  mainType: StoppedVehicleStatisticsMainType,
  expansionChartType: StoppedVehicleStatisticsExpansionChartType,
  searchComparisonDateTime: SearchComparisonDateTime,
  graphLang: GraphLang,
  searchGraphIn?: Partial<VehicleData>,
  comparisonGraphIn?: VehicleData,
  timeInterval: TimeInterval = TIME_INTERVAL.HOUR_1,
  useComparisonDate = false,
  seriesTitle = "",
  comparisonSeriesTitle = "",
  enableDataZoom = true
): EChartsOption => {
  let searchGraph: Partial<VehicleData> = {
    car: [],
    bus: [],
    truck: [],
    motorcycle: [],
    bicycle: []
  };
  let comparisonGraph: VehicleData = {
    car: [],
    bus: [],
    truck: [],
    motorcycle: [],
    bicycle: []
  };
  if (searchGraphIn !== undefined) {
    searchGraph = {
      car: searchGraphIn?.car,
      bus: searchGraphIn?.bus,
      truck: searchGraphIn?.truck,
      motorcycle: searchGraphIn?.motorcycle,
      bicycle: searchGraphIn?.bicycle
    };
  }
  if (comparisonGraphIn !== undefined) {
    comparisonGraph = {
      car: comparisonGraphIn?.car,
      bus: comparisonGraphIn?.bus,
      truck: comparisonGraphIn?.truck,
      motorcycle: comparisonGraphIn?.motorcycle,
      bicycle: comparisonGraphIn?.bicycle
    };
  }
  const dataSearch: number[] =
    searchGraph?.car?.map((_data, index) => {
      return (
        ((searchGraph.car && searchGraph.car[index]) || 0) +
        ((searchGraph.bus && searchGraph.bus[index]) || 0) +
        ((searchGraph.truck && searchGraph.truck[index]) || 0) +
        ((searchGraph.motorcycle && searchGraph.motorcycle[index]) || 0) +
        ((searchGraph.bicycle && searchGraph.bicycle[index]) || 0)
      );
    }) || [];
  const dataComparison: number[] =
    comparisonGraph?.car?.map((_data, index) => {
      return (
        ((comparisonGraph.car && comparisonGraph.car[index]) || 0) +
        ((comparisonGraph.bus && comparisonGraph.bus[index]) || 0) +
        ((comparisonGraph.truck && comparisonGraph.truck[index]) || 0) +
        ((comparisonGraph.motorcycle && comparisonGraph.motorcycle[index]) ||
          0) +
        ((comparisonGraph.bicycle && comparisonGraph.bicycle[index]) || 0)
      );
    }) || [];
  const { mode, start, end } = searchComparisonDateTime;
  const result = getAxisIntervalLabels(mode, start, end, timeInterval);
  const { labels: xAxisLabels } = result;

  if (
    expansionChartType === CHART_TYPE.LINE &&
    mainType === WIDGET_MAIN_TYPE.SUMMARY
  ) {
    return {
      tooltip: tooltipSettingLineSummary(timeInterval, end, mode),
      grid: {
        left: 25,
        right: 25,
        bottom: "0",
        top: 35,
        containLabel: true,
        show: true,
        borderColor: "transparent"
      },
      dataZoom: enableDataZoom
        ? [
            {
              type: "inside"
            }
          ]
        : [],
      xAxis: [
        {
          type: "category",
          nameTextStyle: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            verticalAlign: "top",
            lineHeight: 25,
            padding: [1, 0, 0, 0]
          },
          nameGap: 11,
          data: xAxisLabels,
          boundaryGap: false,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            formatter: xAxisLabelFormatter(timeInterval, mode)
          },
          axisLine: {
            show: false
          },
          axisTick: {
            lineStyle: {
              color: theme.widget.stoppedVehicleStatistics.graphLineColor,
              width: 1
            }
          }
        }
      ],
      yAxis: [
        {
          type: "value",
          nameTextStyle: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right",
            padding: [0, 6, 0, 0]
          },
          nameGap: 12,
          splitNumber: 2,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right",
            hideOverlap: true
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.widget.stoppedVehicleStatistics.graphLineColor,
              width: 1
            }
          }
        }
      ],
      series: [
        ...[
          {
            name: seriesTitle,
            type: "line",
            data: dataSearch,
            itemStyle: {
              color:
                comparisonType === COMPARISON_TYPE.DECREASE
                  ? theme.widget.stoppedVehicleStatistics.systemRed
                  : theme.widget.stoppedVehicleStatistics.systemBlue
            },
            symbolSize: 6,
            z: SERIES_Z_VALUE.ORIGINAL_DATA
          } as LineSeriesOption
        ],
        ...(useComparisonDate
          ? [
              {
                name: comparisonSeriesTitle,
                type: "line",
                data: dataComparison,
                itemStyle: {
                  color: theme.widget.stoppedVehicleStatistics.graphBar
                },
                lineStyle: {
                  color: theme.widget.stoppedVehicleStatistics.graphBar,
                  width: 2
                },
                symbolSize: 7,
                symbol: "circle"
              } as LineSeriesOption
            ]
          : [])
      ]
    };
  }

  if (expansionChartType === CHART_TYPE.LINE) {
    return {
      title: {
        show: false
      },
      color: [
        theme.widget.stoppedVehicleStatistics.car,
        theme.widget.stoppedVehicleStatistics.bus,
        theme.widget.stoppedVehicleStatistics.truck,
        theme.widget.stoppedVehicleStatistics.motorcycle,
        theme.widget.stoppedVehicleStatistics.bicycle
      ],
      tooltip: tooltipSettingLine(timeInterval, mode),
      grid: {
        left: 25,
        right: 25,
        bottom: "0",
        top: 35,
        containLabel: true,
        show: true,
        borderColor: "transparent"
      },
      dataZoom: enableDataZoom
        ? [
            {
              type: "inside"
            }
          ]
        : [],
      xAxis: [
        {
          type: "category",
          nameTextStyle: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            verticalAlign: "top",
            lineHeight: 25,
            padding: [1, 0, 0, 0]
          },
          nameGap: 10,
          data: xAxisLabels,
          boundaryGap: false,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            formatter: xAxisLabelFormatter(timeInterval, mode)
          },
          axisLine: {
            show: false
          },
          axisTick: {
            lineStyle: {
              color: theme.widget.stoppedVehicleStatistics.graphLineColor,
              width: 1
            }
          }
        }
      ],
      yAxis: [
        {
          type: "value",
          nameTextStyle: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right",
            padding: [0, 6, 0, 0]
          },
          nameGap: 12,
          splitNumber: 2,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right",
            hideOverlap: true
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.widget.stoppedVehicleStatistics.graphLineColor,
              width: 1
            }
          }
        }
      ],
      series: [
        {
          name: graphLang?.car,
          type: "line",
          data: searchGraph?.car,
          lineStyle: {
            width: 2
          },
          symbolSize: 4
        },
        {
          name: graphLang?.bus,
          type: "line",
          data: searchGraph?.bus,
          lineStyle: {
            width: 2
          },
          symbolSize: 4
        },
        {
          name: graphLang?.truck,
          type: "line",
          data: searchGraph?.truck,
          lineStyle: {
            width: 2
          },
          symbolSize: 4
        },
        {
          name: graphLang?.motorcycle,
          type: "line",
          data: searchGraph?.motorcycle,
          lineStyle: {
            width: 2
          },
          symbolSize: 4
        },
        {
          name: graphLang?.bicycle,
          type: "line",
          data: searchGraph?.bicycle,
          lineStyle: {
            width: 2
          },
          symbolSize: 4
        }
      ]
    };
  }

  const getVehicleCalculation = (vehicle: number[] | undefined): number => {
    return timeInterval === TIME_INTERVAL.HOUR_1 ||
      timeInterval === TIME_INTERVAL.DAY_1 ||
      timeInterval === TIME_INTERVAL.DAY_7 ||
      timeInterval === TIME_INTERVAL.MONTH_1
      ? (_.last(vehicle) as number)
      : sum(vehicle);
  };

  const sumSearch = {
    car: getVehicleCalculation(searchGraph.car),
    bus: getVehicleCalculation(searchGraph.bus),
    truck: getVehicleCalculation(searchGraph.truck),
    motorcycle: getVehicleCalculation(searchGraph.motorcycle),
    bicycle: getVehicleCalculation(searchGraph.bicycle)
  };
  const sumAllSearch = sum(Object.values(sumSearch));
  const percentSearch = [
    +getPercent(sumSearch.car, sumAllSearch),
    +getPercent(sumSearch.bus, sumAllSearch),
    +getPercent(sumSearch.truck, sumAllSearch),
    +getPercent(sumSearch.motorcycle, sumAllSearch),
    +getPercent(sumSearch.bicycle, sumAllSearch)
  ];

  const sumComparison = {
    car: getVehicleCalculation(comparisonGraph.car),
    bus: getVehicleCalculation(comparisonGraph.bus),
    truck: getVehicleCalculation(comparisonGraph.truck),
    motorcycle: getVehicleCalculation(comparisonGraph.motorcycle),
    bicycle: getVehicleCalculation(comparisonGraph.bicycle)
  };
  const sumAllComparison = sum(Object.values(sumComparison));
  const percentComparison = [
    +getPercent(sumComparison.car, sumAllComparison),
    +getPercent(sumComparison.bus, sumAllComparison),
    +getPercent(sumComparison.truck, sumAllComparison),
    +getPercent(sumComparison.motorcycle, sumAllComparison),
    +getPercent(sumComparison.bicycle, sumAllComparison)
  ];

  const maxPercent = (max(concat(percentSearch, percentComparison)) || 0) + 10;

  return {
    title: {
      show: false
    },
    tooltip: tooltipRadar(theme, graphLang, [sumSearch, sumComparison]),
    legend: {
      left: 0,
      icon: "circle",
      itemWidth: ECHART_VEHICLE_STATISTICS_SIZE,
      itemHeight: ECHART_VEHICLE_STATISTICS_SIZE,
      orient: "vertical",
      textStyle: {
        fontSize: "12px",
        fontFamily: HVTheme.fontFamily,
        color: theme.widget.stoppedVehicleStatistics.textPrimary
      },
      data: [
        {
          name: graphLang?.labelSearch || "",
          itemStyle: {
            color: theme.widget.stoppedVehicleStatistics.today
          }
        },
        {
          name: graphLang?.labelComparison || "",
          itemStyle: {
            color: theme.widget.stoppedVehicleStatistics.yesterday,
            borderColor: theme.widget.stoppedVehicleStatistics.yesterday,
            borderWidth: 2
          }
        }
      ]
    },
    radar: {
      scale: true,
      indicator: [
        {
          name: graphLang?.car,
          max: maxPercent,
          color: theme.widget.stoppedVehicleStatistics.textSecondary
        },
        {
          name: graphLang?.bus,
          max: maxPercent,
          color: theme.widget.stoppedVehicleStatistics.textSecondary
        },
        {
          name: graphLang?.truck,
          max: maxPercent,
          color: theme.widget.stoppedVehicleStatistics.textSecondary
        },
        {
          name: graphLang?.motorcycle,
          max: maxPercent,
          color: theme.widget.stoppedVehicleStatistics.textSecondary
        },
        {
          name: graphLang?.bicycle,
          max: maxPercent,
          color: theme.widget.stoppedVehicleStatistics.textSecondary
        }
      ],
      splitNumber: 4,
      axisName: {
        fontSize: "10px",
        fontFamily: HVTheme.fontFamily
      },
      splitArea: {
        areaStyle: {
          color: [theme.widget.stoppedVehicleStatistics.radarSplitArea]
        }
      },
      axisLine: {
        lineStyle: {
          color: theme.widget.stoppedVehicleStatistics.lineRadar
        }
      },
      splitLine: {
        lineStyle: {
          color: theme.widget.stoppedVehicleStatistics.lineRadar
        }
      }
    },
    series: [
      {
        type: "radar",
        data: [
          ...[
            {
              value: percentSearch,
              name: graphLang?.labelSearch || "",
              itemStyle: {
                color: theme.widget.stoppedVehicleStatistics.white,
                borderColor: theme.widget.stoppedVehicleStatistics.today,
                borderWidth: 2
              },
              lineStyle: {
                color: theme.widget.stoppedVehicleStatistics.today
              }
            }
          ],
          ...(useComparisonDate
            ? [
                {
                  value: percentComparison,
                  name: graphLang?.labelComparison,
                  itemStyle: {
                    color: theme.widget.stoppedVehicleStatistics.yesterday
                  },
                  lineStyle: {
                    color: theme.widget.stoppedVehicleStatistics.yesterday
                  }
                }
              ]
            : [])
        ]
      }
    ]
  };
};

export const mainChart = (
  theme: Theme,
  chartType: StoppedVehicleStatisticsMainChartType,
  timeInterval: TimeInterval,
  searchGraph?: Partial<VehicleData>,
  search?: number,
  fromAnalytics?: boolean
): EChartsOption => {
  const objectKeys = searchGraph && Object.keys(searchGraph);
  const sumData: Record<string, number> = {};
  const percent: Record<string, string> = {};
  const percentRounding: Record<string, string> = {};
  const data: VehicleArrayType[] = [];
  const barStackCategoryseries: BarSeriesOption[] = [];
  const barStackColor: string[] = [];
  const barChartXaxisData: string[] = [];
  const barSeriesData: BarSeriesDataType[] = [];
  let barSeriesMarkerPoint;
  let barSeriesIconMarkerPoint;
  const getColorByVehicleType = (vehicle: string) => {
    if (vehicle === "car") {
      return theme.widget.stoppedVehicleStatistics.car;
    }
    if (vehicle === "truck") {
      return theme.widget.stoppedVehicleStatistics.truck;
    }
    if (vehicle === "bus") {
      return theme.widget.stoppedVehicleStatistics.bus;
    }
    if (vehicle === "motorcycle") {
      return theme.widget.stoppedVehicleStatistics.motorcycle;
    }
    if (vehicle === "bicycle") {
      return theme.widget.stoppedVehicleStatistics.bicycle;
    }

    return "";
  };
  const calculateTotalOfLastValues = (
    searchGraph: Partial<VehicleData> | undefined
  ) => {
    return (
      searchGraph &&
      ((searchGraph.car && searchGraph.car[searchGraph.car.length - 1]) || 0) +
        ((searchGraph.bus && searchGraph.bus[searchGraph.bus.length - 1]) ||
          0) +
        ((searchGraph.truck &&
          searchGraph.truck[searchGraph.truck.length - 1]) ||
          0) +
        ((searchGraph.motorcycle &&
          searchGraph.motorcycle[searchGraph.motorcycle.length - 1]) ||
          0) +
        ((searchGraph.bicycle &&
          searchGraph.bicycle[searchGraph.bicycle.length - 1]) ||
          0)
    );
  };

  const renderSymbolByVehicletype = (vehicle: string) => {
    if (vehicle === "car") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg width="50" height="2" viewBox="0 0 50 2" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="50" height="2" fill="#00B960"/>
      </svg>`
      )}`;
    }
    if (vehicle === "truck") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg width="50" height="2" viewBox="0 0 50 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="50" height="2" fill="#FFB400"/>
        </svg>
        `
      )}`;
    }
    if (vehicle === "bus") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg width="50" height="2" viewBox="0 0 50 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="50" height="2" fill="#4891FF"/>
        </svg>
        `
      )}`;
    }
    if (vehicle === "motorcycle") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg width="51" height="2" viewBox="0 0 51 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="51" height="2" fill="#FF6D10"/>
                  </svg>
                  `
      )}`;
    }
    if (vehicle === "bicycle") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg width="51" height="2" viewBox="0 0 51 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="51" height="2" fill="#ff598e"/>
        </svg>                  
        `
      )}`;
    }
    return "";
  };
  const renderMarkerIconSymbol = (vehicle: string) => {
    if (vehicle === "car") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M6.745 4.59692V6.842" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.0972 4.77521L3.44348 6.842" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.32898 4.59692H7.64961C8.12504 4.59692 8.58727 4.7488 8.98346 5.03934L11.2946 6.71654C11.4134 6.80238 11.5455 6.85521 11.6776 6.88162" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.84375 4.59692H6.32946" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.6776 6.88159L13.1303 7.13251C13.6124 7.21175 14.0746 7.46927 14.3585 7.90508C14.517 8.1494 14.6028 8.40693 14.6028 8.69086V8.86915C14.6028 9.43042 14.1868 9.88604 13.6718 9.88604H12.7011H10.2778H5.14048H3.37744H2.32753C1.81248 9.87943 1.39648 9.42381 1.39648 8.86254V8.24845" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M1.39648 8.24848C1.39648 8.24848 1.52855 4.59692 4.84334 4.59692" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M1.7002 6.84198L11.6776 6.8816" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.69856 11.2001C5.42793 11.2001 6.0192 10.6088 6.0192 9.87947C6.0192 9.15011 5.42793 8.55884 4.69856 8.55884C3.9692 8.55884 3.37793 9.15011 3.37793 9.87947C3.37793 10.6088 3.9692 11.2001 4.69856 11.2001Z" fill="none" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.3012 11.2001C12.0306 11.2001 12.6219 10.6088 12.6219 9.87947C12.6219 9.15011 12.0306 8.55884 11.3012 8.55884C10.5719 8.55884 9.98059 9.15011 9.98059 9.87947C9.98059 10.6088 10.5719 11.2001 11.3012 11.2001Z" fill="none" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`
      )}`;
    }
    if (vehicle === "truck") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M10.4763 5.26489V7.79065H13.3294" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.6744 10.2176H13.5579V7.69804L12.3599 5.59839H10.4764V4.04218H2.44214V10.2176H3.69575" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.6228 10.372H9.80357" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.60381 11.4527C5.1154 11.4527 5.53012 11.0379 5.53012 10.5264C5.53012 10.0148 5.1154 9.60004 4.60381 9.60004C4.09222 9.60004 3.67749 10.0148 3.67749 10.5264C3.67749 11.0379 4.09222 11.4527 4.60381 11.4527Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.7786 11.4527C11.2902 11.4527 11.7049 11.0379 11.7049 10.5264C11.7049 10.0148 11.2902 9.60004 10.7786 9.60004C10.267 9.60004 9.85229 10.0148 9.85229 10.5264C9.85229 11.0379 10.267 11.4527 10.7786 11.4527Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
        `
      )}`;
    }
    if (vehicle === "bus") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M5.0603 4.5755V7.13213" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.6543 4.5755V7.13213" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.2419 4.5755V7.13213" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.1438 10.541H13.0392C13.3233 10.541 13.5579 10.3125 13.5579 10.0284V7.51501C13.5579 7.26182 13.5394 7.00862 13.4962 6.76161C13.2862 5.50182 12.1808 4.5755 10.8901 4.5755H3.43638C2.88677 4.5755 2.44214 5.01396 2.44214 5.5574V9.69494L4.60972 10.541" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.31409 10.541H10.4331" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.14702 11.3685C5.65861 11.3685 6.07333 10.9538 6.07333 10.4422C6.07333 9.93059 5.65861 9.51587 5.14702 9.51587C4.63543 9.51587 4.2207 9.93059 4.2207 10.4422C4.2207 10.9538 4.63543 11.3685 5.14702 11.3685Z" fill="none" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.97 11.3686C11.4816 11.3686 11.8963 10.9538 11.8963 10.4422C11.8963 9.93066 11.4816 9.51593 10.97 9.51593C10.4584 9.51593 10.0437 9.93066 10.0437 10.4422C10.0437 10.9538 10.4584 11.3686 10.97 11.3686Z" fill="none" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.2417 7.13214H2.44214" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.2419 7.13214C10.2419 7.13214 10.8348 8.76863 13.5582 8.76863" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg> `
      )}`;
    }
    if (vehicle === "motorcycle") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12.3231 9.60007L11.9526 4.30772L10.4705 4.04218" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12.3229 5.27728H12.9404" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6.14746 10.8351H11.094" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3.46109 10.8351C3.46109 10.8351 2.44214 10.7425 2.44214 9.26658C2.44214 7.79065 5.10993 7.12988 6.64761 7.12988C6.64761 8.70462 6.88228 9.98294 7.38249 10.6746" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2.44214 7.12988H8.00003" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.91258 12.0702C5.5947 12.0702 6.14767 11.5172 6.14767 10.8351C6.14767 10.153 5.5947 9.60004 4.91258 9.60004C4.23046 9.60004 3.67749 10.153 3.67749 10.8351C3.67749 11.5172 4.23046 12.0702 4.91258 12.0702Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12.3229 12.0703C13.005 12.0703 13.5579 11.5173 13.5579 10.8352C13.5579 10.1531 13.005 9.6001 12.3229 9.6001C11.6407 9.6001 11.0878 10.1531 11.0878 10.8352C11.0878 11.5173 11.6407 12.0703 12.3229 12.0703Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                  `
      )}`;
    }
    if (vehicle === "bicycle") {
      return `image://data:image/svg+xml;base64,${btoa(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3.8398 11.6267C4.90019 11.6267 5.7598 10.7671 5.7598 9.70674C5.7598 8.64636 4.90019 7.78674 3.8398 7.78674C2.77941 7.78674 1.9198 8.64636 1.9198 9.70674C1.9198 10.7671 2.77941 11.6267 3.8398 11.6267Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.1601 11.6267C13.2205 11.6267 14.0801 10.7671 14.0801 9.70668C14.0801 8.6463 13.2205 7.78668 12.1601 7.78668C11.0997 7.78668 10.2401 8.6463 10.2401 9.70668C10.2401 10.7671 11.0997 11.6267 12.1601 11.6267Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.2398 6.50671H5.75984L3.83984 9.70671H8.31984L10.2398 6.50671Z" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.29871 5.28424L8.31951 9.70664" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.1601 9.70673L10.2401 6.50673V5.22673L11.5201 4.58673" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.47937 5.22662H6.69377" stroke="${theme.widget.stoppedVehicleStatistics.textSecondary2}" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>                  
        `
      )}`;
    }
    return "";
  };
  const borderSearchData = (
    objects: string[],
    searchGraph: Partial<VehicleData>
  ) => {
    const dataSearch: number[] = [];
    objects.forEach((elem, index) => {
      if (sum(searchGraph[elem as keyof VehicleData]) !== 0) {
        dataSearch.push(index);
      }
    });
    return dataSearch;
  };
  if (objectKeys?.length && searchGraph) {
    objectKeys.forEach((elem, index) => {
      sumData[elem] =
        timeInterval === TIME_INTERVAL.HOUR_1 ||
        timeInterval === TIME_INTERVAL.DAY_1 ||
        timeInterval === TIME_INTERVAL.DAY_7 ||
        timeInterval === TIME_INTERVAL.MONTH_1
          ? (_.last(searchGraph[elem as keyof VehicleData]) as number)
          : sum(searchGraph[elem as keyof VehicleData]);
      percent[elem] = getPercent(
        sumData[elem as keyof VehicleData],
        search && calculateTotalOfLastValues(searchGraph),
        10
      );
      percentRounding[elem] = (+percent[elem as keyof VehicleData]).toFixed(0);
      const colorByVehicle = getColorByVehicleType(elem);
      barStackColor.push(colorByVehicle);
      data.push({ name: elem, value: sumData[elem as keyof VehicleData] });
      const borderStyle = (index: number) => {
        const dataSearch = borderSearchData(objectKeys, searchGraph);
        if (dataSearch.length === 1) {
          return [4, 4, 4, 4];
        }
        if (dataSearch.length > 1 && index === dataSearch[0]) {
          return [4, 0, 0, 4];
        }
        if (
          dataSearch.length > 1 &&
          index === dataSearch[dataSearch.length - 1]
        ) {
          return [0, 4, 4, 0];
        }
        return [0, 0, 0, 0];
      };
      barStackCategoryseries.push({
        name: elem,
        type: "bar",
        stack: "Total",
        label: {
          show: !fromAnalytics,
          formatter: (params: DefaultLabelFormatterCallbackParams) => {
            return (params.value as number) >= 10
              ? `${Number(params.value as number).toFixed(0)}%`
              : "";
          },
          fontSize: 12,
          fontWeight: 500,
          color: theme.widget.stoppedVehicleStatistics.textLabelBarStack,
          fontFamily: HVTheme.fontFamily,
          position: "insideLeft"
        },
        barWidth: 20,
        data: [percent[elem]],
        itemStyle: {
          borderRadius: borderStyle(index),
          borderWidth: 1,
          borderColor:
            theme.widget.stoppedVehicleStatistics.backgroundColorTooltip
        },
        color: getColorByVehicleType(elem)
      });

      barChartXaxisData.push(elem);
      barSeriesData.push({
        value: percent[elem as keyof VehicleData],
        itemStyle: {
          color: `rgba(${convertHexToRgb(getColorByVehicleType(elem))}, 0.2)`
        }
      });
      const markerSeriesData = {
        name: elem,
        value: percentRounding[elem as keyof VehicleData],
        xAxis: index,
        yAxis: percentRounding[elem as keyof VehicleData],
        symbol: renderSymbolByVehicletype(elem)
      };
      const markerSeriesIconData = {
        name: elem,
        value: percentRounding[elem as keyof VehicleData],
        xAxis: index,
        yAxis: percentRounding[elem as keyof VehicleData],
        symbol: renderMarkerIconSymbol(elem)
      };
      if (barSeriesMarkerPoint?.length) {
        barSeriesMarkerPoint.push(markerSeriesData);
      } else {
        barSeriesMarkerPoint = [markerSeriesData];
      }
      if (barSeriesIconMarkerPoint?.length) {
        barSeriesIconMarkerPoint.push(markerSeriesIconData);
      } else {
        barSeriesIconMarkerPoint = [markerSeriesIconData];
      }
    });
  }
  if (chartType === CHART_TYPE.BAR_CATEGORY_STACK) {
    return {
      title: {
        show: false
      },
      tooltip: {
        show: false
      },
      legend: {
        show: false
      },
      grid: {
        left: 0,
        right: 0,
        top: 70,
        containLabel: false,
        show: false,
        width: "100%"
      },
      xAxis: [
        {
          type: "value",
          show: false,
          max: 100,
          splitLine: { show: false },
          axisLine: { show: false }
        }
      ],
      yAxis: [
        {
          type: "category",
          show: false,
          data: ["Vehicle"]
        }
      ],
      series: barStackCategoryseries
    };
  }

  if (chartType === CHART_TYPE.BAR) {
    return {
      title: {
        show: false
      },
      tooltip: {
        show: false
      },
      legend: {
        show: false
      },
      grid: {
        left: "0%",
        right: "0%",
        bottom: 5,
        top: "40%",
        show: true,
        containLabel: false,
        borderColor: "transparent",
        height: "55%"
      },
      xAxis: [
        {
          type: "category",
          show: false,
          data: barChartXaxisData
        }
      ],
      yAxis: [
        {
          type: "value",
          show: false
        }
      ],
      series: [
        {
          name: "Stopped vehicle analytics",
          type: "bar",
          markPoint: {
            label: {
              show: false
            },
            symbolSize: [50, 2],
            emphasis: {
              disabled: true
            },
            data: barSeriesMarkerPoint
          },
          data: barSeriesData,
          colorBy: "data",
          barWidth: 50,
          barGap: "-100%"
        },
        {
          name: "Stopped vehicle analytics icon",
          type: "bar",
          markPoint: {
            label: {
              show: true,
              fontSize: 12,
              color: theme.widget.stoppedVehicleStatistics.textPrimary,
              formatter: (params) => {
                return `${params.value}%`;
              },
              align: "left",
              verticalAlign: "middle",
              distance: 10,
              offset: [10, 1]
            },
            emphasis: {
              disabled: true
            },
            symbolSize: [16, 16],
            symbolOffset: [-15, -10],
            data: barSeriesIconMarkerPoint
          },
          data: barSeriesData,
          colorBy: "data",
          barWidth: 50,
          barGap: "-100%"
        }
      ]
    };
  }
  const total = data.reduce(
    (sum: number, current: StoppedVehicleStatisticsDataPieChart[0]): number => {
      return sum + current.value;
    },
    0
  );

  return {
    legend: {
      type: "scroll",
      orient: "vertical",
      right: 0,
      bottom: 20,
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 8,
      show: true,
      icon: "circle",
      data,
      selectedMode: false,
      formatter: (params) => {
        const value = data.filter((row) => row.name === params)[0].value;
        const percent = Math.round((value / total) * 100);
        const percentPreview = percent >= 0 ? percent : "-";

        return `{d|${params}} {a|${percentPreview}%}`;
      },
      textStyle: {
        color: theme.widget.stoppedVehicleStatistics.textSecondary,
        overflow: "truncate",
        fontSize: "12px",
        rich: {
          d: {
            width: 70
          },
          a: {
            align: "right",
            width: 30
          }
        }
      }
    },
    series: [
      {
        name: "Stopped vehicle analytics",
        type: "pie",
        center: ["25%", "50%"],
        color: barStackColor,
        radius: ["60%", "70%"],
        label: {
          show: false,
          position: "center",
          formatter: (params) => {
            if (isNumber(params.percent) && params.data) {
              return `{d|${Math.round(params.percent)}%}\n{a|${
                (params.data as DefaultLabelFormatterCallbackParams).name
              }} {b||} {a|${
                (params.data as DefaultLabelFormatterCallbackParams).value
              }}`;
            }
            return "";
          },
          rich: {
            b: {
              color: theme.widget.stoppedVehicleStatistics.borderColorTooltip
            },
            a: {
              fontSize: 12,
              color: theme.widget.stoppedVehicleStatistics.textSecondary
            },
            d: {
              fontSize: 28,
              color: theme.widget.stoppedVehicleStatistics.textPrimary
            }
          },
          fontFamily: HVTheme.fontFamily
        },
        data,
        itemStyle: {
          borderWidth: 2,
          borderColor:
            theme.widget.stoppedVehicleStatistics.backgroundColorTooltip
        },
        emphasis: {
          label: {
            show: true
          }
        }
      }
    ]
  };
};
